import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, getIn } from "formik";
import { toast } from "react-toastify";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import MicIcon from "@material-ui/icons/Mic";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import { DataGrid } from "@material-ui/data-grid";
import AudioRecorder from "../AudioRecorder";

import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Chip,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import { getTokens } from "../../styles/tokens";
import { usePanelStyles, ModalTitle, FormSection } from "../FormFields/panel";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
    mediaContainer: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    mediaHint: {
      margin: 0,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: t.textSecondary,
    },
    mediaOptions: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
    },
    mediaInfo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "8px 8px 8px 12px",
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
    },
    mediaName: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
      fontSize: 13.5,
      color: t.text,
      "& > span:nth-child(2)": {
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& .MuiSvgIcon-root": { fontSize: 18, color: t.textSecondary },
    },
    existingMediaActions: {
      display: "flex",
      gap: 2,
      alignItems: "center",
      flex: "none",
    },
    mediaPreview: {
      maxWidth: "100%",
      maxHeight: 200,
      alignSelf: "flex-start",
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
    },
    componentRow: { display: "flex", flexDirection: "column", gap: 6 },
    grid: {
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      "& .MuiDataGrid-columnsContainer": { backgroundColor: t.muted },
    },
  };
});

const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required("Obrigatório"),
  isOficial: Yup.boolean(),
  components: Yup.array().when("isOficial", {
    is: true,
    then: Yup.array().of(
      Yup.object().shape({
        text: Yup.string().trim().required("Obrigatório"),
      })
    ),
    otherwise: Yup.array().notRequired(),
  }),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const classes = useStyles();
  const p = usePanelStyles();
  const { user } = useContext(AuthContext);

  const messageInputRef = useRef();

  const initialState = {
    shortcode: "",
    message: "",
    geral: true,
    visao: true,
    isOficial: false,
    status: "",
    language: "",
    category: "",
    metaID: "",
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [mediaMode, setMediaMode] = useState(null); // 'file', 'audio', 'edit', null
  const [isEditingMedia, setIsEditingMedia] = useState(false);
  const attachmentFile = useRef(null);

  useEffect(() => {
    try {
      (async () => {
        if (!quickemessageId) return;

        const { data } = await api.get(`/quick-messages/${quickemessageId}`);

        setQuickemessage((prevState) => {
          return { ...prevState, ...data };
        });

        // Reset media editing state when loading existing message
        setIsEditingMedia(false);
        setMediaMode(null);
      })();
    } catch (err) {
      toastError(err);
    }
  }, [quickemessageId, open]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
      setAudioBlob(null); // Limpar áudio se existir
      setMediaMode('file');
      setIsEditingMedia(true);
    }
  };

  const handleAudioRecorded = (blob) => {
    setAudioBlob(blob);
    setAttachment(null); // Limpar arquivo se existir
    setMediaMode('audio');
    setIsEditingMedia(true);
  };

  const handleAudioDeleted = () => {
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const handleCancelEdit = () => {
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    if (attachmentFile.current) {
      attachmentFile.current.value = null;
    }
  };

  const handleEditExistingMedia = () => {
    setIsEditingMedia(true);
    setMediaMode('edit');
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  const handleSaveQuickeMessage = async (values) => {
    const quickemessageData = {
      ...values,
      isMedia: !!(attachment || audioBlob || (quickemessage.mediaPath && !isEditingMedia)),
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
          ? getBasename(values.mediaPath).replace(/ /g, "_")
          : null,
      isOficial: quickemessageId ? values.isOficial : false,
    };

    // Inclui componentes editados (template oficial)
    if (Array.isArray(values.components)) {
      quickemessageData.components = values.components.map(c => ({ id: c.id, text: c.text }));
    }

    try {
      let quickMessageRecord;

      if (quickemessageId) {
        quickMessageRecord = await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        quickMessageRecord = { data };
      }

      const messageId = quickemessageId || quickMessageRecord.data.id;

      // Se está editando mídia, primeiro remover a mídia antiga
      if (isEditingMedia && quickemessage.mediaPath) {
        await api.delete(`/quick-messages/${messageId}/media-upload`);
      }

      // Upload de arquivo comum
      if (attachment) {
        const formData = new FormData();
        formData.append("typeArch", "quickMessage"); // ✅ IMPORTANTE
        formData.append("file", attachment);

        console.log("📤 Enviando arquivo:", {
          name: attachment.name,
          type: attachment.type,
          size: attachment.size
        });

        await api.post(`/quick-messages/${messageId}/media-upload`, formData);
      }

      // ✅ CORREÇÃO: Upload de áudio gravado
      if (audioBlob) {
        const formData = new FormData();
        formData.append("typeArch", "quickMessage"); // ✅ IMPORTANTE para o multer
        formData.append("audio", audioBlob, `audio_${Date.now()}.webm`); // ✅ fieldname = 'audio'

        console.log("🎵 Enviando áudio gravado:", {
          size: audioBlob.size,
          type: audioBlob.type
        });

        const response = await api.post(`/quick-messages/${messageId}/audio-upload`, formData);
        console.log("✅ Resposta do upload de áudio:", response.data);
      }

      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload === "function") {
        reload();
      }
    } catch (err) {
      console.error("❌ Erro ao salvar quick message:", err);
      toastError(err);
    }
    handleClose();
  };

  const rowsWithIds = (quickemessage?.components || []).map((component, index) => ({
    id: index,
    ...component,
  }));

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (audioBlob) {
      setAudioBlob(null);
    }

    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({
        ...prev,
        mediaPath: null,
        mediaName: null,
        mediaType: null
      }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload === "function") {
        reload();
      }
    }

    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'audio': return '🎵';
      case 'image': return '🖼️';
      case 'video': return '🎥';
      default: return '📎';
    }
  };

  const getMediaPreview = (quickmessage) => {
    if (!quickmessage.mediaPath) return null;

    const mediaUrl = `${getBackendUrl() || 'http://localhost:8080'}/public/company${user.companyId}/quickMessage/${quickmessage.mediaName}`;

    if (quickmessage.mediaType === 'image') {
      return (
        <img
          src={mediaUrl}
          alt={quickmessage.mediaName}
          className={classes.mediaPreview}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }

    //if (quickmessage.mediaType === 'audio') {
    //  return (
    //    <audio 
    //      controls 
    //      className={classes.mediaPreview}
    //      src={mediaUrl}
    //   >
    //      Seu navegador não suporta o elemento de áudio.
    //    </audio>
    //  );
    //}

    if (quickmessage.mediaType === 'video') {
      return (
        <video
          controls
          className={classes.mediaPreview}
          src={mediaUrl}
        >
          Seu navegador não suporta o elemento de vídeo.
        </video>
      );
    }

    return null;
  };

  // Verificar se há nova mídia sendo adicionada
  const hasNewMedia = attachment || audioBlob;
  // Verificar se há mídia existente (e não está sendo editada)
  const hasExistingMedia = quickemessage.mediaPath && !isEditingMedia;
  // Verificar se há qualquer tipo de mídia
  const hasAnyMedia = hasNewMedia || hasExistingMedia;

  return (
    <>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: p.dialogPaper }}
      >
        <ModalTitle
          id="form-dialog-title"
          title={quickemessageId
            ? `${i18n.t("quickMessages.dialog.edit")}`
            : `${i18n.t("quickMessages.dialog.add")}`}
          subtitle="Atalho, texto e mídia que entram na conversa ao escolher a resposta."
          onClose={handleClose}
        />
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={{
            ...quickemessage,
            isStarter: quickemessage?.isStarter || false,
          }}
          enableReinitialize={true}
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickeMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => {
            // Função para verificar se os campos estão desabilitados
            const isDisabled = quickemessageId &&
              values.visao &&
              !values.geral &&
              values.userId !== user.id;

            // sem a aba "Oficial" visível, o corpo é sempre o da aba geral
            const activeTab = values.isOficial ? tabIndex : 0;

            return (
              <Form className={p.modalForm}>
                {/* com uma aba só (resposta comum) a barra de abas não diz nada */}
                {values.isOficial && (
                  <Tabs
                    className={p.modalTabs}
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    <Tab label={i18n.t("quickMessages.dialog.general")} />
                    <Tab label="Oficial" />
                  </Tabs>
                )}
                <DialogContent className={clsx(p.modalBody, p.labelsAbove)}>
                  {activeTab === 0 && (
                    <>
                      <FormSection>
                        <div className={p.stack}>
                          <Field
                            as={TextField}
                            autoFocus
                            label={i18n.t("quickMessages.dialog.shortcode")}
                            name="shortcode"
                            disabled={isDisabled}
                            error={touched.shortcode && Boolean(errors.shortcode)}
                            helperText={touched.shortcode && errors.shortcode}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.message")}
                            name="message"
                            inputRef={messageInputRef}
                            error={touched.message && Boolean(errors.message)}
                            helperText={touched.message && errors.message}
                            variant="outlined"
                            size="small"
                            disabled={isDisabled}
                            multiline={true}
                            rows={7}
                            fullWidth
                          />
                          <MessageVariablesPicker
                            disabled={isSubmitting || isDisabled}
                            showSchedulingVars={true}
                            onClick={(value) =>
                              handleClickMsgVar(value, setFieldValue)
                            }
                          />
                        </div>
                      </FormSection>

                      {/* Seção de Mídia */}
                      <FormSection title="Anexar Mídia">
                        <div className={classes.mediaContainer}>
                          {/* Mídia existente */}
                          {hasExistingMedia && (
                            <>
                              <div className={classes.mediaInfo}>
                                <div className={classes.mediaName}>
                                  <span aria-hidden="true">{getMediaTypeIcon(quickemessage.mediaType)}</span>
                                  <span>{quickemessage.mediaName}</span>
                                  {quickemessage.mediaType && (
                                    <Chip
                                      size="small"
                                      label={quickemessage.mediaType}
                                      variant="outlined"
                                    />
                                  )}
                                </div>
                                <div className={classes.existingMediaActions}>
                                  <Tooltip title="Editar mídia">
                                    <span>
                                      <IconButton
                                        onClick={handleEditExistingMedia}
                                        size="small"
                                        aria-label="Editar mídia"
                                        disabled={isDisabled}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Remover mídia">
                                    <span>
                                      <IconButton
                                        onClick={() => setConfirmationOpen(true)}
                                        size="small"
                                        aria-label="Remover mídia"
                                        disabled={isDisabled}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </div>
                              </div>

                              {/* Preview da mídia existente */}
                              {getMediaPreview(quickemessage)}
                            </>
                          )}

                          {/* Nova mídia (arquivo selecionado) */}
                          {attachment && (
                            <div className={classes.mediaInfo}>
                              <div className={classes.mediaName}>
                                <AttachFileIcon />
                                <span>{attachment.name}</span>
                                <Chip size="small" label="Novo Arquivo" color="primary" />
                              </div>
                              <IconButton
                                onClick={() => {
                                  setAttachment(null);
                                  setMediaMode(null);
                                  setIsEditingMedia(false);
                                  if (attachmentFile.current) {
                                    attachmentFile.current.value = null;
                                  }
                                }}
                                size="small"
                                aria-label="Remover arquivo"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}

                          {/* Áudio gravado */}
                          {audioBlob && (
                            <div className={classes.mediaInfo}>
                              <div className={classes.mediaName}>
                                <MicIcon />
                                <span>Novo áudio gravado</span>
                                <Chip size="small" label="Novo Áudio" color="primary" />
                              </div>
                              <IconButton
                                onClick={handleAudioDeleted}
                                size="small"
                                aria-label="Remover áudio"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}

                          {/* Opções de mídia quando não há mídia anexada OU quando está editando */}
                          {(!hasAnyMedia || isEditingMedia) && (
                            <>
                              {isEditingMedia && hasExistingMedia && (
                                <p className={classes.mediaHint}>
                                  Escolha uma nova mídia para substituir:
                                </p>
                              )}

                              {!hasAnyMedia && (
                                <p className={classes.mediaHint}>
                                  Escolha uma opção para anexar mídia:
                                </p>
                              )}

                              <div className={classes.mediaOptions}>
                                <Button
                                  variant="outlined"
                                  startIcon={<AttachFileIcon />}
                                  onClick={() => attachmentFile.current.click()}
                                  disabled={isSubmitting || isDisabled}
                                >
                                  Anexar Arquivo
                                </Button>
                                <Button
                                  variant="outlined"
                                  startIcon={<MicIcon />}
                                  onClick={() => setMediaMode('audio')}
                                  disabled={isSubmitting || isDisabled}
                                >
                                  Gravar Áudio
                                </Button>

                                {isEditingMedia && (
                                  <Button
                                    onClick={handleCancelEdit}
                                    disabled={isSubmitting}
                                  >
                                    Cancelar Edição
                                  </Button>
                                )}
                              </div>
                            </>
                          )}

                          {/* Componente de gravação de áudio */}
                          {mediaMode === 'audio' && !audioBlob && (
                            <AudioRecorder
                              onAudioRecorded={handleAudioRecorded}
                              onAudioDeleted={handleAudioDeleted}
                              disabled={isSubmitting || isDisabled}
                            />
                          )}
                        </div>
                      </FormSection>

                      <FormSection title="Quem vê e quem edita">
                        <div className={p.grid2}>
                          <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="visao-selection-label">
                              {i18n.t("quickMessages.dialog.visao")}
                            </InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("quickMessages.dialog.visao")}
                              placeholder={i18n.t("quickMessages.dialog.visao")}
                              labelId="visao-selection-label"
                              id="visao"
                              disabled={isDisabled}
                              name="visao"
                              onChange={(e) => {
                                setFieldValue("visao", e.target.value === "true");
                              }}
                              error={touched.visao && Boolean(errors.visao)}
                              value={values.visao ? "true" : "false"}
                            >
                              <MenuItem value={"true"}>
                                {i18n.t("announcements.active")}
                              </MenuItem>
                              <MenuItem value={"false"}>
                                {i18n.t("announcements.inactive")}
                              </MenuItem>
                            </Field>
                          </FormControl>
                          {values.visao === true && (
                            <FormControl
                              variant="outlined"
                              size="small"
                              fullWidth
                            >
                              <InputLabel id="novo-item-selection-label">
                                {i18n.t("quickMessages.dialog.geral")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("quickMessages.dialog.geral")}
                                placeholder={i18n.t("quickMessages.dialog.geral")}
                                labelId="novo-item-selection-label"
                                id="geral"
                                name="geral"
                                disabled={isDisabled}
                                value={values.geral ? "true" : "false"}
                                error={touched.geral && Boolean(errors.geral)}
                              >
                                <MenuItem value={"true"}>
                                  {i18n.t("announcements.active")}
                                </MenuItem>
                                <MenuItem value={"false"}>
                                  {i18n.t("announcements.inactive")}
                                </MenuItem>
                              </Field>
                            </FormControl>
                          )}
                        </div>
                      </FormSection>
                    </>
                  )}
                  {activeTab === 1 && (
                    <>
                      <FormSection title="Componentes do template">
                        <div className={p.stack}>
                          {values.isOficial && (
                            <FormControlLabel
                              control={
                                <Field
                                  name="isStarter"
                                  type="checkbox"
                                  as={Checkbox}
                                  color="primary"
                                />
                              }
                              label="Template inicia conversa com o cliente"
                            />
                          )}
                          <DataGrid
                            className={classes.grid}
                            rows={rowsWithIds || []}
                            columns={[
                              { field: "type", headerName: "Tipo", width: 150 },
                              { field: "text", headerName: "Valor", width: 400 },
                            ]}
                            pageSize={5}
                            disableSelectionOnClick
                            autoHeight={true}
                          />
                          {/* Edição dos textos do template (Oficial) */}
                          {values.isOficial && (values.components || []).map((comp, idx) => (
                            <div key={comp.id || idx} className={classes.componentRow}>
                              <Field
                                as={TextField}
                                label={`Texto (${comp.type || 'component'})`}
                                name={`components.${idx}.text`}
                                variant="outlined"
                                size="small"
                                fullWidth
                                error={Boolean(getIn(touched, `components.${idx}.text`) && getIn(errors, `components.${idx}.text`))}
                                helperText={getIn(touched, `components.${idx}.text`) && getIn(errors, `components.${idx}.text`)}
                              />
                            </div>
                          ))}
                        </div>
                      </FormSection>
                      <FormSection title="Dados na Meta">
                        <div className={p.grid2}>
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.status")}
                            name="status"
                            disabled={values.isOficial}
                            error={touched.status && Boolean(errors.status)}
                            helperText={touched.status && errors.status}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.language")}
                            name="language"
                            disabled={values.isOficial}
                            error={touched.language && Boolean(errors.language)}
                            helperText={touched.language && errors.language}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.category")}
                            name="category"
                            disabled={values.isOficial}
                            error={touched.category && Boolean(errors.category)}
                            helperText={touched.category && errors.category}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                          <Field
                            as={TextField}
                            label={i18n.t("quickMessages.dialog.metaID")}
                            name="metaID"
                            disabled={values.isOficial}
                            error={touched.metaID && Boolean(errors.metaID)}
                            helperText={touched.metaID && errors.metaID}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                        </div>
                      </FormSection>
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {i18n.t("quickMessages.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || isDisabled}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {quickemessageId
                      ? `${i18n.t("quickMessages.buttons.edit")}`
                      : `${i18n.t("quickMessages.buttons.add")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};

export default QuickMessageDialog;
